import { TestCardContainer, TestVerifyResultStyles } from '@Src/_basic/components/MuiStyleComponent/TestComponent';
import ColFieldInfo from '@Src/_basic/components/YatungTest/ColFieldInfo';
import EditButtons from '@Src/_basic/components/YatungTest/EditButtons';
import TestTable from '@Src/_basic/components/YatungTest/TestTable';
import { checkIsNaN } from '@Src/_basic/helpers/CheckIsNaN';
import { formatNumber } from '@Src/_basic/helpers/FormatNumber';
import { basicNumberSchema } from '@Src/_basic/helpers/TestForm';
import { isCustomStandardPassed, isLegalStandardPassed, isRejected } from '@Src/_basic/helpers/VerifyResultHelper';
import { AllTest } from '@Src/_basic/object/TestType';
import {
  ChemicalChloride,
  ImportChemicalChloride,
  PutChemicalChlorideRequest,
} from '@Src/_basic/object/test/cementtitiousLiquid/ChemicalChlorideType';
import { TestApi } from '@Src/_basic/protocol/test/TestApi';
import LabelInput from '@Src/app/module/proportionManage/components/LabelInput';
import { Stack } from '@mui/material';
import { useFormik } from 'formik';
import React, { ChangeEvent, memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

interface Props {
  testDetail: ChemicalChloride;
  // eslint-disable-next-line no-unused-vars
  afterUpdateTest: (test: AllTest) => void;
  canEdit?: boolean;
}

const validationSchema = Yup.object().shape({
  chlorideIonContent: basicNumberSchema,
});

function ExportChemicalChlorideTable({ testDetail, afterUpdateTest, canEdit }: Props) {
  const { t: i18T } = useTranslation();
  const [edit, setEdit] = useState<boolean>(false);

  const initialValues = useMemo(
    () => ({
      chlorideIonContent: testDetail.chlorideIonContent,
    }),
    [testDetail],
  );

  const isLegalPassed = useMemo(() => {
    return isLegalStandardPassed(testDetail.verifyPassedResults);
  }, [testDetail.verifyPassedResults]);

  const isCustomPassed = useMemo(() => {
    return isCustomStandardPassed(testDetail.verifyCustomResults);
  }, [testDetail.verifyCustomResults]);

  const isCustomRejected = useMemo(() => {
    return isRejected(testDetail.verifyCustomResults);
  }, [testDetail.verifyCustomResults]);

  const verifyResultText = useMemo(() => {
    const tempText: Array<string> = [];
    if (isLegalPassed && isCustomPassed && !isCustomRejected) {
      tempText.push(i18T(`TEST.BASE.NORMAL`));
    } else {
      isLegalPassed ? tempText.push(i18T(`TEST.BASE.CNS_NORMAL`)) : tempText.push(i18T(`TEST.BASE.CNS_ABNORMAL`));

      if (isCustomRejected) {
        tempText.push(i18T(`TEST.BASE.CUSTOM_REJECTED`));
      } else {
        isCustomPassed
          ? tempText.push(i18T(`TEST.BASE.CUSTOM_NORMAL`))
          : tempText.push(i18T(`TEST.BASE.CUSTOM_ABNORMAL`));
      }
    }
    return tempText;
  }, [i18T, isLegalPassed, isCustomPassed, isCustomRejected]);

  const firstHeader = useMemo(
    () => ({
      title: `${i18T(`TEST.CHEMICAL_CHLORIDE.CHLORIDE`)}(%)`,
    }),
    [i18T],
  );

  const firstInfo = useMemo(
    () => [
      {
        title: `${formatNumber(testDetail?.chlorideIonContent?.toFixed(4))}`,
        titleStyle:
          isLegalPassed && isCustomPassed && !isCustomRejected
            ? TestVerifyResultStyles.normal
            : TestVerifyResultStyles.abnormal,
      },
    ],
    [testDetail?.chlorideIonContent, isLegalPassed, isCustomPassed, isCustomRejected],
  );

  const secondHeader = useMemo(
    () => ({
      title: i18T(`TEST.BASE.DETECTION`),
    }),
    [i18T],
  );

  const secondInfo = useMemo(
    () => [
      isLegalPassed && isCustomPassed && !isCustomRejected
        ? {
            titles: verifyResultText,
            titlesStyle: [TestVerifyResultStyles.normal],
          }
        : {
            titles: verifyResultText,
            titlesStyle: [
              isLegalPassed ? TestVerifyResultStyles.normal : TestVerifyResultStyles.abnormal,
              isCustomPassed && !isCustomRejected ? TestVerifyResultStyles.normal : TestVerifyResultStyles.abnormal,
            ],
          },
    ],
    [verifyResultText, isLegalPassed, isCustomPassed, isCustomRejected],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      const data: ImportChemicalChloride = {
        chlorideIonContent: checkIsNaN(+values.chlorideIonContent),
      };

      TestApi.putTest<PutChemicalChlorideRequest>(
        {
          exptItemCode: testDetail.exptItemCode,
          fields: {
            ...testDetail,
            ...data,
          },
        },
        (_data) => {
          afterUpdateTest(_data);
          setEdit(false);
        },
        (err) => console.log(err),
      );
    },
  });

  const chlorideIonContentView = useMemo(() => {
    switch (edit) {
      case true:
        return (
          <ColFieldInfo
            titleStyle={{ ...TestVerifyResultStyles.resultTitle, borderBottom: '1px solid #ccc', paddingBottom: 20 }}
            title={`${i18T(`TEST.RECYCLE_CHLORIDE.CHLORIDE`)}(%)`}
          >
            <LabelInput
              valueWidth="150px"
              value={formik.values.chlorideIonContent}
              isError={!!(formik.touched.chlorideIonContent && formik.errors.chlorideIonContent)}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                formik.setFieldValue('chlorideIonContent', e.target.value)
              }
            />
          </ColFieldInfo>
        );
      case false:
        return <TestTable headerData={firstHeader} rows={firstInfo} />;
      default:
        break;
    }
  }, [edit, formik, firstInfo, firstHeader, i18T]);

  return (
    <Stack gap={3}>
      <TestCardContainer>
        <EditButtons edit={edit} setEdit={setEdit} onSubmit={formik.handleSubmit} canEdit={canEdit} />
        {chlorideIonContentView}
      </TestCardContainer>
      <TestCardContainer>
        <TestTable headerData={secondHeader} rows={secondInfo} />
      </TestCardContainer>
    </Stack>
  );
}

export default memo(ExportChemicalChlorideTable);
